import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  Search, 
  BookOpen, 
  Clock, 
  User,
  Filter,
  CheckCircle
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const CourseCatalog = () => {
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [enrollingId, setEnrollingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [coursesRes, enrollmentsRes] = await Promise.all([
        axios.get(`${API}/courses`, { withCredentials: true }),
        axios.get(`${API}/enrollments`, { withCredentials: true })
      ]);
      setCourses(coursesRes.data);
      setEnrollments(enrollmentsRes.data);
    } catch (error) {
      toast.error("Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (courseId) => {
    setEnrollingId(courseId);
    try {
      await axios.post(`${API}/enrollments`, { course_id: courseId }, { withCredentials: true });
      toast.success("Successfully enrolled!");
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to enroll");
    } finally {
      setEnrollingId(null);
    }
  };

  const isEnrolled = (courseId) => {
    return enrollments.some(e => e.course_id === courseId);
  };

  const categories = [...new Set(courses.map(c => c.category).filter(Boolean))];

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === "all" || course.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const thumbnails = [
    "https://images.unsplash.com/photo-1758413353832-7ae844fd7d50?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHNoYXBlcyUyMGVkdWNhdGlvbnxlbnwwfHx8fDE3NzA0MzQ3MTF8MA&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1766083274533-0013e10828f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHNoYXBlcyUyMGVkdWNhdGlvbnxlbnwwfHx8fDE3NzA0MzQ3MTF8MA&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1576842234179-523181b29d79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHNoYXBlcyUyMGVkdWNhdGlvbnxlbnwwfHx8fDE3NzA0MzQ3MTF8MA&ixlib=rb-4.1.0&q=85"
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in" data-testid="course-catalog">
        {/* Header */}
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: 'Outfit' }}>
            Course Catalog
          </h1>
          <p className="text-stone-600 mt-1">Discover and enroll in courses that interest you</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
            <Input
              data-testid="search-courses-input"
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-12 rounded-xl"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full md:w-48 h-12 rounded-xl" data-testid="category-filter">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Course Grid */}
        {filteredCourses.length === 0 ? (
          <Card className="bg-white rounded-xl border border-stone-100">
            <CardContent className="p-12 text-center">
              <BookOpen className="h-16 w-16 text-stone-300 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-stone-900 mb-2">No courses found</h3>
              <p className="text-stone-500">Try adjusting your search or filters</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCourses.map((course, index) => {
              const enrolled = isEnrolled(course.course_id);
              return (
                <Card 
                  key={course.course_id}
                  className="group relative overflow-hidden rounded-2xl bg-white border border-stone-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  data-testid={`course-${course.course_id}`}
                >
                  <div className="aspect-video bg-stone-100 overflow-hidden">
                    <img 
                      src={course.thumbnail || thumbnails[index % thumbnails.length]}
                      alt={course.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {enrolled && (
                      <div className="absolute top-4 right-4 bg-emerald-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        Enrolled
                      </div>
                    )}
                  </div>
                  <CardContent className="p-6">
                    <Badge variant="secondary" className="mb-3 bg-violet-100 text-violet-700">
                      {course.category || 'General'}
                    </Badge>
                    <h3 className="text-xl font-semibold text-stone-900 mb-2 line-clamp-2" style={{ fontFamily: 'Outfit' }}>
                      {course.title}
                    </h3>
                    <p className="text-stone-600 text-sm mb-4 line-clamp-2">
                      {course.description}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-stone-500 mb-4">
                      <div className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        {course.instructor}
                      </div>
                      {course.duration && (
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {course.duration}
                        </div>
                      )}
                    </div>
                    {enrolled ? (
                      <Link to={`/courses/${course.course_id}`}>
                        <Button 
                          className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-xl"
                          data-testid={`continue-course-${course.course_id}`}
                        >
                          Continue Learning
                        </Button>
                      </Link>
                    ) : (
                      <Button 
                        className="w-full bg-violet-700 hover:bg-violet-800 rounded-xl"
                        onClick={() => handleEnroll(course.course_id)}
                        disabled={enrollingId === course.course_id}
                        data-testid={`enroll-course-${course.course_id}`}
                      >
                        {enrollingId === course.course_id ? "Enrolling..." : "Enroll Now"}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CourseCatalog;
